import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { changeRequestsApi } from '../api/changeRequests';
import type { ChangeRequestsListFilters } from '../api/changeRequests';
import { ApiError } from '../api/client';
import type { ChangeRequest, ChangeRequestDecision, ChangeRequestStatus, Page, UUID } from '../api/types';

export const crKeys = {
  all: ['change-requests'] as const,
  // #47 (2026-06-12): the key carries the FULL filter set, not only the
  // status — a filter change must be a distinct cache entry (C-131).
  listByStatus: (status?: ChangeRequestStatus, filters?: object) =>
    ['change-requests', 'list', status, filters ?? {}] as const,
  detail: (id: UUID) => ['change-requests', 'detail', id] as const,
};

export function useChangeRequestsList(
  status?: ChangeRequestStatus,
  // Feature 2 (2026-05-28): the MyChangeRequestsPage cares about updates
  // that happen in OTHER user sessions (Compliance approving). The global
  // QueryClient has staleTime=30s + refetchOnWindowFocus=false, which is
  // fine for most pages but means this page can show stale "Pending"
  // even after Compliance has approved. The opts let a caller opt into
  // freshness-on-mount + polling without changing global defaults.
  opts?: { refetchOnMount?: boolean | 'always'; refetchInterval?: number; staleTime?: number },
  // #47 (2026-06-12): per-column predicate filters (C-132), server-side.
  // limit/offset added in the same change — the Compliance queue rendered
  // a Pagination control that never reached the server (latent C-131-class
  // gap: control without a consumer).
  filters?: Omit<ChangeRequestsListFilters, 'status'> & { limit?: number; offset?: number },
) {
  return useQuery<Page<ChangeRequest>, ApiError>({
    queryKey: crKeys.listByStatus(status, filters),
    queryFn: () => {
      const { limit, offset, ...rest } = filters ?? {};
      return changeRequestsApi.list({ status, ...rest }, limit ?? 50, offset ?? 0);
    },
    refetchOnMount: opts?.refetchOnMount,
    refetchInterval: opts?.refetchInterval,
    staleTime: opts?.staleTime,
    placeholderData: keepPreviousData,
  });
}

export function useChangeRequest(id: UUID | undefined) {
  return useQuery<ChangeRequest, ApiError>({
    queryKey: crKeys.detail(id ?? ''),
    queryFn: () => changeRequestsApi.get(id!),
    enabled: !!id,
  });
}

export function useApproveChangeRequest() {
  const qc = useQueryClient();
  return useMutation<ChangeRequest, ApiError, { id: UUID; input?: ChangeRequestDecision }>({
    mutationFn: ({ id, input }) => changeRequestsApi.approve(id, input),
    onSuccess: (cr) => {
      qc.invalidateQueries({ queryKey: crKeys.all });
      qc.setQueryData(crKeys.detail(cr.id), cr);
    },
  });
}

export function useRejectChangeRequest() {
  const qc = useQueryClient();
  return useMutation<ChangeRequest, ApiError, { id: UUID; input?: ChangeRequestDecision }>({
    mutationFn: ({ id, input }) => changeRequestsApi.reject(id, input),
    onSuccess: (cr) => {
      qc.invalidateQueries({ queryKey: crKeys.all });
      qc.setQueryData(crKeys.detail(cr.id), cr);
    },
  });
}

export function useCancelChangeRequest() {
  const qc = useQueryClient();
  return useMutation<ChangeRequest, ApiError, { id: UUID; input?: ChangeRequestDecision }>({
    mutationFn: ({ id, input }) => changeRequestsApi.cancel(id, input),
    onSuccess: (cr) => {
      qc.invalidateQueries({ queryKey: crKeys.all });
      qc.setQueryData(crKeys.detail(cr.id), cr);
    },
  });
}
